import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Spin } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { renderTemplate, iframeDoc, bodyPreheader, SUB_LABEL } from "../logic";
import { CePage } from "../styles";

const strip = (s) => s.replace(/<[^>]+>/g, "");
const MARK = "data-ce-focus"; // wraps the value being edited, so the preview can find it
const FLASH_MS = 2500;

// The variable being edited, wrapped in an invisible marker (text / HTML values; links and
// images are found by their URL instead). An empty value shows the template default.
const markFocus = (data, focus, template) => {
  const info = focus && template?.variables.find((v) => v.key === focus.key);
  if (!info || info.in_href || info.in_src) return data;
  const value = data[focus.key] ?? info.defaults[0];
  if (!value) return data;
  const tag = /<(p|div|ul|ol|h[1-6]|table)[\s>]/i.test(value) ? "div" : "span"; // HTML blocks need a block wrapper
  return { ...data, [focus.key]: `<${tag} ${MARK}="">${value}</${tag}>` };
};

/** Right-hand live preview: the OneSignal template rendered with the first recipient's custom_data
 * (sample values until a recipient is added). */
const EmailPreview = ({ template, preview, loading, error, previewUser, mode, onMode, focus }) => {
  const data = useMemo(() => preview?.custom_data || {}, [preview]);
  const email = previewUser?.email || "";
  const focusKey = focus?.key;
  const shown = useMemo(() => markFocus(data, focusKey && { key: focusKey }, template), [data, focusKey, template]);
  const html = useMemo(() => renderTemplate(template?.body, shown, email), [template?.body, shown, email]);
  const frameRef = useRef(null);
  const [flash, setFlash] = useState(null); // "subject" | "preheader" while that inbox line is highlighted
  const [open, setOpen] = useState(false); // the full-size live preview popup

  // While a variable is being edited: scroll the preview to where it shows and highlight it for a moment.
  const showFocus = useCallback(() => {
    if (!focus || Date.now() - focus.at > FLASH_MS + 1500 || !template) return;
    const info = template.variables.find((v) => v.key === focus.key);
    if (!info) return;
    if (info.places.subject || info.places.preheader) {
      setFlash(info.places.subject ? "subject" : "preheader");
      setTimeout(() => setFlash(null), FLASH_MS);
    }
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!doc?.body) return;
    let el = doc.querySelector(`[${MARK}]`);
    if (!el && (info.in_href || info.in_src) && data[info.key]) {
      const attr = info.in_href ? "href" : "src";
      el = [...doc.querySelectorAll(`[${attr}]`)].find((e) => e.getAttribute(attr) === data[info.key]);
    }
    if (!el) return;
    frame.scrollIntoView({ block: "nearest" });
    const page = doc.scrollingElement;
    page.scrollTo({ top: Math.max(0, page.scrollTop + el.getBoundingClientRect().top - frame.clientHeight / 3), behavior: "smooth" });
    Object.assign(el.style, { outline: "3px solid #f59e0b", outlineOffset: "2px", backgroundColor: "#fef3c7", transition: "all .4s" });
    setTimeout(() => Object.assign(el.style, { outline: "", outlineOffset: "", backgroundColor: "" }), FLASH_MS);
  }, [focus, template, data]);
  useEffect(() => {
    const t = setTimeout(showFocus, 80); // the preview may not reload (same HTML), so also run on every edit
    return () => clearTimeout(t);
  }, [focus?.at]); // eslint-disable-line react-hooks/exhaustive-deps -- once per edit
  const subject = useMemo(() => strip(renderTemplate(template?.subject, data, email)), [template?.subject, data, email]);
  const preheader = useMemo(() => {
    const fromField = strip(renderTemplate(template?.preheader, data, email));
    return fromField || bodyPreheader(html);
  }, [template?.preheader, data, email, html]);
  const links = preview?.links || [];

  return (
    <aside className="scard pv" aria-live="polite">
      <div className="pvhead">
        <b>Preview {loading && <Spin size="small" style={{ marginLeft: 6 }} />}</b>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Button size="small" icon={<EyeOutlined />} disabled={!template} onClick={() => setOpen(true)}>Live preview</Button>
          <div className="seg" role="group" aria-label="Preview width">
            <button type="button" aria-pressed={mode === "desktop"} onClick={() => onMode("desktop")}>Desktop</button>
            <button type="button" aria-pressed={mode === "mobile"} onClick={() => onMode("mobile")}>Mobile</button>
          </div>
        </div>
      </div>
      {!template ? (
        <div className="sbody note">Pick a template to see the preview.</div>
      ) : (
        <>
          <div className="inbox">
            <span className="av" aria-hidden="true">T</span>
            <div style={{ minWidth: 0 }}>
              <div className={`subj ${flash === "subject" ? "flash" : ""}`}>{template.subject ? subject : <span className="pill bad">No subject in OneSignal template</span>}</div>
              <div className="note">To {email || "each recipient"}</div>
              {preheader && <div className={`pre ${flash === "preheader" ? "flash" : ""}`}>{preheader}</div>}
            </div>
          </div>
          {error && <div className="warnbox" style={{ borderRadius: 0 }}>⚠ {error}</div>}
          <div className="pvmeta note">
            {previewUser ? <>Showing <b>{previewUser.name}</b>'s values (the first recipient).</> : "Showing sample values: [First name], [Last name] and the first itinerary picked. Add recipients to see a real user's values."}
          </div>
          <div className={`mailcanvas ${mode}`}>
            <div className={mode === "mobile" ? "phone" : undefined}>
              {/* no scripts run in it; same-origin only so the page can scroll to / highlight the edited value */}
              <iframe ref={frameRef} title="Email preview" sandbox="allow-same-origin" srcDoc={iframeDoc(html)} onLoad={showFocus} />
            </div>
          </div>
          <details className="links" open={links.length <= 6}>
            <summary>Tracked links ({links.length})</summary>
            {links.map((l, i) => (
              <div className="lk" key={i}>
                <span className="tag">{SUB_LABEL[l.type] || l.type}</span>
                <span><b>{l.label}</b> <span className="muted">· {l.from}</span></span>
                <span className="muted" style={{ fontSize: 11.5 }}>→</span>
                <span className="u">{l.url || "No itinerary for this user, so the link is left out"}</span>
              </div>
            ))}
            {!links.length && <div className="note">No tracked links yet.</div>}
            <div className="note" style={{ marginTop: 8 }}>
              Preview and test emails use direct test links, so nothing is tracked. A real send swaps every link for a tracked one. Links hard-coded in the OneSignal template (footer, social, unsubscribe) aren't tracked.
            </div>
          </details>
        </>
      )}
      <Modal open={open && !!template} onCancel={() => setOpen(false)} footer={null} width="min(1100px, 96vw)" title="Live preview" destroyOnClose>
        <CePage>
          <div className="pvhead" style={{ borderBottom: 0, padding: "0 0 12px" }}>
            <span className="note">{previewUser ? `Showing ${previewUser.name}'s values` : "Showing sample values"}</span>
            <div className="seg" role="group" aria-label="Preview width">
              <button type="button" aria-pressed={mode === "desktop"} onClick={() => onMode("desktop")}>Desktop</button>
              <button type="button" aria-pressed={mode === "mobile"} onClick={() => onMode("mobile")}>Mobile</button>
            </div>
          </div>
          <div className={`mailcanvas ${mode}`}>
            <div className={mode === "mobile" ? "phone" : undefined}>
              <iframe title="Live preview" sandbox="allow-same-origin" srcDoc={iframeDoc(html)} style={{ height: "72vh" }} />
            </div>
          </div>
        </CePage>
      </Modal>
    </aside>
  );
};

export default EmailPreview;
